import { create } from 'zustand'
import { FrontendRestaurant } from '../services/restaurant/restaurantService'
import { getUserDefault, setUserDefault } from '../services/userdefaults/userDefaultService'
// Type-only: keeps the report/display module out of this store's runtime graph.
import type { LeakageUnitMode } from '../components/pages/inventory/leakagePoolDisplay'

export type FilterOption = 'all' | 'food' | 'nonFood'

export const ALL_RESTAURANTS_ID = '__ALL_RESTAURANTS__'
export const SHOW_EX_VAT_KEY = 'show_ex_vat'
export const SHOW_EX_VAT_ORDERS_KEY = 'show_ex_vat_orders'
export const TABLE_ITEMS_PER_PAGE_KEY = 'table_items_per_page'
export const DEFAULT_TABLE_ITEMS_PER_PAGE = 100
export const APP_PAGES_EXPANDED_KEY = 'app_pages_expanded'
export const LEAKAGE_QTY_UNIT_KEY = 'leakage_qty_unit'

/** Stored value → mode. Anything but the opt-in literal reads as today's behaviour. */
export const parseLeakageQtyUnit = (value: string | undefined): LeakageUnitMode =>
  value === 'count' ? 'count' : 'std'

export interface AllRestaurantsOption {
  id: typeof ALL_RESTAURANTS_ID
  name: string
  userId: string
  createdAt: string
  updatedAt: string
}

export function createAllRestaurantsOption(userId: string): AllRestaurantsOption {
  return {
    id: ALL_RESTAURANTS_ID,
    name: 'All Restaurants',
    userId,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  }
}

export function isAllRestaurantsSelected(restaurant: FrontendRestaurant | AllRestaurantsOption | null): restaurant is AllRestaurantsOption {
  return restaurant?.id === ALL_RESTAURANTS_ID
}

interface AppUserDefaultState {
  restaurant: FrontendRestaurant | AllRestaurantsOption | null
  setRestaurant: (restaurant: FrontendRestaurant | AllRestaurantsOption) => void
  filter: FilterOption
  setFilter: (filter: FilterOption) => void
  showExVat: boolean
  setShowExVat: (showExVat: boolean) => void
  isVatSettingLoaded: boolean
  showExVatOrders: boolean
  setShowExVatOrders: (showExVat: boolean) => void
  isVatOrdersSettingLoaded: boolean
  tableItemsPerPage: number
  setTableItemsPerPage: (itemsPerPage: number) => void
  isTableItemsPerPageLoaded: boolean
  appPagesExpanded: boolean
  setAppPagesExpanded: (expanded: boolean) => void
  isAppPagesExpandedLoaded: boolean
  leakageQtyUnit: LeakageUnitMode
  setLeakageQtyUnit: (mode: LeakageUnitMode) => void
  isLeakageQtyUnitLoaded: boolean
  isLoading: boolean
  isLoaded: boolean

  hasSentToPayment: boolean
  setHasSentToPayment: (hasSentToPayment: boolean) => void
}

export const useAppUserDefaultStore = create<AppUserDefaultState>(set => ({
  restaurant: null,
  setRestaurant: restaurant => set({ restaurant }),
  filter: 'all',
  setFilter: filter => set({ filter }),
  showExVat: true, // Default to showing excl VAT for revenue
  setShowExVat: showExVat => set({ showExVat }),
  isVatSettingLoaded: false,
  showExVatOrders: true, // Default to showing excl VAT for orders
  setShowExVatOrders: showExVatOrders => set({ showExVatOrders }),
  isVatOrdersSettingLoaded: false,
  tableItemsPerPage: DEFAULT_TABLE_ITEMS_PER_PAGE,
  setTableItemsPerPage: tableItemsPerPage => set({ tableItemsPerPage }),
  isTableItemsPerPageLoaded: false,
  appPagesExpanded: false, // Default to collapsed
  setAppPagesExpanded: appPagesExpanded => set({ appPagesExpanded }),
  isAppPagesExpandedLoaded: false,
  leakageQtyUnit: 'std', // Default to the standardised base units (kg / L / pcs)
  setLeakageQtyUnit: leakageQtyUnit => set({ leakageQtyUnit }),
  isLeakageQtyUnitLoaded: false,
  isLoading: false,
  isLoaded: false,
  hasSentToPayment: false,
  setHasSentToPayment: hasSentToPayment => set({ hasSentToPayment }),
}))

// Utility functions for VAT setting
export async function loadVatSetting(): Promise<void> {
  const state = useAppUserDefaultStore.getState()
  
  // Skip if already loaded
  if (state.isVatSettingLoaded) {
    return
  }
  
  try {
    const def = await getUserDefault(SHOW_EX_VAT_KEY)
    const showExVat = def?.value !== 'false' // Default to true (excl VAT) if not set or value is not 'false'
    useAppUserDefaultStore.setState({ showExVat, isVatSettingLoaded: true })
  } catch (error) {
    console.warn('Failed to load VAT setting, using default')
    useAppUserDefaultStore.setState({ showExVat: true, isVatSettingLoaded: true })
  }
}

export async function saveVatSetting(showExVat: boolean): Promise<void> {
  try {
    await setUserDefault(SHOW_EX_VAT_KEY, showExVat.toString())
    useAppUserDefaultStore.getState().setShowExVat(showExVat)
  } catch (error) {
    console.error('Failed to save VAT setting:', error)
    throw error
  }
}

// Utility functions for Orders VAT setting
export async function loadVatOrdersSetting(): Promise<void> {
  const state = useAppUserDefaultStore.getState()

  // Skip if already loaded
  if (state.isVatOrdersSettingLoaded) {
    return
  }

  try {
    const def = await getUserDefault(SHOW_EX_VAT_ORDERS_KEY)
    const showExVatOrders = def?.value !== 'false' // Default to true if not set or value is not 'false'
    useAppUserDefaultStore.setState({ showExVatOrders, isVatOrdersSettingLoaded: true })
  } catch (error) {
    console.warn('Failed to load Orders VAT setting, using default')
    useAppUserDefaultStore.setState({ showExVatOrders: true, isVatOrdersSettingLoaded: true })
  }
}

export async function saveVatOrdersSetting(showExVatOrders: boolean): Promise<void> {
  try {
    await setUserDefault(SHOW_EX_VAT_ORDERS_KEY, showExVatOrders.toString())
    useAppUserDefaultStore.getState().setShowExVatOrders(showExVatOrders)
  } catch (error) {
    console.error('Failed to save Orders VAT setting:', error)
    throw error
  }
}

// Utility functions for the Leakage tab's quantity-unit setting
export async function loadLeakageQtyUnitSetting(): Promise<void> {
  const state = useAppUserDefaultStore.getState()

  // Skip if already loaded
  if (state.isLeakageQtyUnitLoaded) {
    return
  }

  try {
    const def = await getUserDefault(LEAKAGE_QTY_UNIT_KEY)
    useAppUserDefaultStore.setState({ leakageQtyUnit: parseLeakageQtyUnit(def?.value), isLeakageQtyUnitLoaded: true })
  } catch (error) {
    console.warn('Failed to load Leakage quantity unit setting, using default')
    useAppUserDefaultStore.setState({ leakageQtyUnit: 'std', isLeakageQtyUnitLoaded: true })
  }
}

export async function saveLeakageQtyUnitSetting(mode: LeakageUnitMode): Promise<void> {
  try {
    await setUserDefault(LEAKAGE_QTY_UNIT_KEY, mode)
    useAppUserDefaultStore.getState().setLeakageQtyUnit(mode)
  } catch (error) {
    console.error('Failed to save Leakage quantity unit setting:', error)
    throw error
  }
}

// Utility functions for Table Items Per Page setting
export async function loadTableItemsPerPageSetting(): Promise<void> {
  const state = useAppUserDefaultStore.getState()

  // Skip if already loaded
  if (state.isTableItemsPerPageLoaded) {
    return
  }

  try {
    const def = await getUserDefault(TABLE_ITEMS_PER_PAGE_KEY)
    const savedItemsPerPage = Number(def?.value)
    const tableItemsPerPage = Number.isInteger(savedItemsPerPage) && savedItemsPerPage > 0
      ? savedItemsPerPage
      : DEFAULT_TABLE_ITEMS_PER_PAGE
    useAppUserDefaultStore.setState({ tableItemsPerPage, isTableItemsPerPageLoaded: true })
  } catch (error) {
    console.warn('Failed to load Table Items Per Page setting, using default')
    useAppUserDefaultStore.setState({ tableItemsPerPage: DEFAULT_TABLE_ITEMS_PER_PAGE, isTableItemsPerPageLoaded: true })
  }
}

export async function saveTableItemsPerPageSetting(itemsPerPage: number): Promise<void> {
  try {
    await setUserDefault(TABLE_ITEMS_PER_PAGE_KEY, itemsPerPage.toString())
    useAppUserDefaultStore.getState().setTableItemsPerPage(itemsPerPage)
  } catch (error) {
    console.error('Failed to save Table Items Per Page setting:', error)
    throw error
  }
}

// Utility functions for App Pages Expanded setting
export async function loadAppPagesExpandedSetting(): Promise<void> {
  const state = useAppUserDefaultStore.getState()

  // Skip if already loaded
  if (state.isAppPagesExpandedLoaded) {
    return
  }

  try {
    const def = await getUserDefault(APP_PAGES_EXPANDED_KEY)
    const appPagesExpanded = def?.value === 'true' // Default to false (collapsed) if not set
    useAppUserDefaultStore.setState({ appPagesExpanded, isAppPagesExpandedLoaded: true })
  } catch (error) {
    console.warn('Failed to load App Pages Expanded setting, using default')
    useAppUserDefaultStore.setState({ appPagesExpanded: false, isAppPagesExpandedLoaded: true })
  }
}

export async function saveAppPagesExpandedSetting(expanded: boolean): Promise<void> {
  try {
    await setUserDefault(APP_PAGES_EXPANDED_KEY, expanded.toString())
    useAppUserDefaultStore.getState().setAppPagesExpanded(expanded)
  } catch (error) {
    console.error('Failed to save App Pages Expanded setting:', error)
    throw error
  }
}
